import { useMemo, useState } from "react";

import { DirectorySearchHelper as DirectorySearchHelper } from "@/ContactDirectory/Helpers/DirectorySearchHelper";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { DisplayFaxNumbers } from "./DisplayFaxNumbers";

interface IDirectorySearchInfoProps {
    entity: IDirectoryEntity;
    onSelectFaxNumber?: (faxNumberId: string) => void;
}

interface IDisplayContactNumberProps {
    entity: IDirectoryEntity;
    onSelectFaxNumber: (faxNumberId: string) => void;
}
function DisplayContactNumbers(props: IDisplayContactNumberProps): JSX.Element {
    const hasPhoneNumbers = props.entity.hasPhoneNumbers();
    const hasFaxNumbers = props.entity.hasFaxNumbers();
    const formattedPhoneNumbers = useMemo(() => hasPhoneNumbers ? DirectorySearchHelper.formatPhoneNumbers(props.entity.getPhoneNumbersForDisplay(false)) : null, []);
    const formattedFaxNumbers = useMemo(() => hasFaxNumbers ? props.entity.getFaxNumbersForDisplay().filter(fax => fax.formattedNumber) : [], []);
    const [showAllFaxNumbers, setShowAllFaxNumbers] = useState<boolean>(false);

    const toolTip = useMemo(
        () => [
            formattedPhoneNumbers?.trim(),
            !showAllFaxNumbers
                ? formattedFaxNumbers.map((p, i) => `${i === 0 ? "F: " : ""}${p.formattedNumber}${p.description ? ` ${p.description}` : ""}`).join(", ").trim()
                : null
        ]
            .filter(p => !!p)
            .join(" | "),
        []
    );

    return (
        <div
            className="ellipsis-auto"
            title={toolTip}
        >
            {hasPhoneNumbers && formattedPhoneNumbers}
            {hasPhoneNumbers && hasFaxNumbers && !showAllFaxNumbers
                && <span className="separator">|</span>
            }
            {!showAllFaxNumbers && formattedFaxNumbers.length > 0 && (
                <span>F:
                    {formattedFaxNumbers.map((p, i) => (
                        <span className="margin-xs-right" key={p.formattedNumber}>
                            {p.formattedNumber}
                            {p.description && <span style={{ fontStyle: "italic" }}> {p.description}</span>}{i !== formattedFaxNumbers.length - 1 ? "," : ""}
                        </span>
                    ))}
                </span>
            )}

            {props.entity.shouldRenderMultipleFaxNumbers() && (
                <div>
                    <DisplayFaxNumbers
                        entity={props.entity}
                        onSelectFaxNumber={faxNumberId => props.onSelectFaxNumber(faxNumberId)}
                        showAllFaxNumbers={showAllFaxNumbers}
                        onShowAllFaxNumbers={showAll => setShowAllFaxNumbers(showAll)}
                    />
                </div>
            )}
        </div>
    );
}

function DirectorySearchInfo(props: IDirectorySearchInfoProps): JSX.Element {
    const formattedAddress = useMemo(() => props.entity.getDirectoryAddressForDisplay(), []);

    return (
        <>
            {formattedAddress && (
                <div
                    className="ellipsis-auto"
                    title={formattedAddress}
                >
                    {formattedAddress}
                </div>
            )}
            <DisplayContactNumbers
                entity={props.entity}
                onSelectFaxNumber={props.onSelectFaxNumber}
            />
        </>
    );
}

export { DirectorySearchInfo };
